import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import TopNav from "../../Components/Dashboards/TopNav";
import Notifications from "../../Components/Dashboards/Notifications";
import NavHeader2 from "../../Components/Dashboards/NavHeader2";
import Sidebar from "../../Components/Dashboards/Sidebar";
import Footer from "../../Components/Dashboards/Footer";
import BreadcrumTop from "../../Components/Dashboards/BreadcrumTop";
import Swal from "sweetalert2";
import { useCreateProduct } from "../../hooks/useProducts";
import { getToken } from "../../utils/getToken";
import { apiFetch } from "../../utils/apiFetch";
import { API_ROUTES } from "../../utils/constants";

export default function CreateProduct(props) {
  const { isLoaded } = props;

  const [companyId, setCompanyId] = useState("");
  const [marketerId, setMarketerId] = useState("");
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [company, setCompany] = useState([]);
  const [marketer, setMarketer] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginUserRole, setLoginUserRole] = useState("");
  const [companyIdForMarketer, setCompanyIdForMarketer] = useState("");

  const navigate = useNavigate();
  const createProductMutation = useCreateProduct();

  useEffect(() => {
    document.title = "Create Product | Dashboard";
  }, []);

  useEffect(() => {
    const storedUserData = localStorage.getItem("userVASData");

    if (storedUserData) {
      try {
        const parsedData = JSON.parse(storedUserData);
        const id = parsedData.responseDetails?.id;
        const userRoles = parsedData.responseDetails?.roles;

        if (userRoles) {
          if (userRoles.includes("Admin")) {
            setLoginUserRole("Admin");
          } else if (userRoles.includes("CompanyUser")) {
            setLoginUserRole("CompanyUser");
            if (id) {
              setCompanyIdForMarketer(id);
              setCompanyId(id); // Set companyId for CompanyUser
            }
          }
        }
      } catch (error) {
        console.error("Error parsing storedUserData:", error);
      }
    }
  }, []);

  const getAllCompany = async () => {
    const token = getToken();
    if (!token) {
      Swal.fire({
        title: "Error",
        text: "Please log in first.",
        icon: "error",
      });
      localStorage.removeItem("companyName");
      localStorage.removeItem("userVASData");
      navigate("/login");
      return;
    }

    setLoading(true);
    try {
      const response = await apiFetch(API_ROUTES.GET_ALL_COMPANY, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch companies");
      }

      const responseJSON = await response.json();
      if (Array.isArray(responseJSON)) {
        const sortedData = responseJSON
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setCompany(sortedData);
      }
    } catch (error) {
      console.error("Error getting companies:", error);
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch marketers based on company
  const getAllCompanyMarketer = async (selectedCompanyId) => {
    const token = getToken();
    if (!token || !selectedCompanyId) return;

    setLoading(true);
    try {
      const url = `${API_ROUTES.GET_ALL_COMPANY_MARKETER}/${encodeURIComponent(
        selectedCompanyId
      )}`;

      const response = await apiFetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch marketers");
      }

      const responseJSON = await response.json();
      if (Array.isArray(responseJSON)) {
        const sortedData = responseJSON
          .slice()
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setMarketer(sortedData);
      }
    } catch (error) {
      console.error("Error getting marketers:", error);
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load companies for Admin
  useEffect(() => {
    if (loginUserRole === "Admin") {
      getAllCompany();
    } else if (loginUserRole === "CompanyUser" && companyIdForMarketer) {
      getAllCompanyMarketer(companyIdForMarketer);
    }
  }, [loginUserRole, companyIdForMarketer]);

  // Fetch marketers when company is selected
  const handleCompanyChange = (e) => {
    const selectedCompanyId = e.target.value;
    setCompanyId(selectedCompanyId);
    setMarketerId(""); // Reset marketer selection
    if (selectedCompanyId) {
      getAllCompanyMarketer(selectedCompanyId);
    }
  };
  const validateForm = () => {
    if (!companyId) return showError("Company is required.");
    if (!marketerId) return showError("Marketer is required.");
    if (!productId) return showError("Product ID is required.");
    if (!productName) return showError("Product Name is required.");

    return true;
  };

  const showError = (message) => {
    Swal.fire({ title: "Error", text: message, icon: "error" });
    return false;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData = {
      companyId,
      marketerId: parseInt(marketerId),
      productId,
      productName,
    };

    createProductMutation.mutate(formData);
  };

  return (
    <>
      <div id="main-wrapper" className={isLoaded ? "show" : ""}>
        <TopNav />
        <Notifications />
        <NavHeader2 />
        <Sidebar />

        <div className="content-body">
          <div className="container-fluid">
            <BreadcrumTop title="Create Product" />

            <div className="row">
              <div className="d-flex justify-content-center align-items-center">
                <div className="col-xl-7 col-lg-10 col-md-10">
                  <div className="card">
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>

                        <div className="mb-3 row">
                          <label className="col-sm-2 col-form-label">
                            Company
                          </label>
                          <div className="col-sm-10">
                            {loginUserRole === "Admin" ? (
                              <select
                                className="form-select py-3_ form-control2"
                                value={companyId}
                                onChange={handleCompanyChange}
                              >
                                <option value="">Select Company</option>
                                {company.map((comp) => (
                                  <option key={comp.id} value={comp.id}>
                                    {comp.companyName}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <input
                                type="text"
                                className="form-select py-3_ form-control2"
                                value={companyId}
                                readOnly
                                placeholder="Company ID"
                              />
                            )}
                          </div>
                        </div>

                        <div className="mb-3 row">
                          <label className="col-sm-2 col-form-label">
                            Marketer
                          </label>
                          <div className="col-sm-10">
                            <select
                              className="form-select py-3_ form-control2"
                              value={marketerId}
                              onChange={(e) => setMarketerId(e.target.value)}
                              disabled={!companyId || loading}
                            >
                              <option value="">Select Marketer</option>
                              {marketer.map((mark) => (
                                <option key={mark.id} value={mark.id}>
                                  {mark.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div className="mb-3 row">
                          <label className="col-sm-2 col-form-label">
                            Product ID
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              name="productId"
                              value={productId}
                              onChange={(e) => setProductId(e.target.value)}
                              className="form-control"
                              placeholder="Enter Product ID"
                            />
                          </div>
                        </div>

                        <div className="mb-3 row">
                          <label className="col-sm-2 col-form-label">
                            Product Name
                          </label>
                          <div className="col-sm-10">
                            <input
                              type="text"
                              name="productName"
                              value={productName}
                              onChange={(e) => setProductName(e.target.value)}
                              className="form-control"
                              placeholder="Enter Product Name"
                            />
                          </div>
                        </div>

                        <div className="mb-3 mt-4 row">
                          <div className="col-sm-12 d-flex justify-content-end">
                            <button
                              type="submit"
                              className="btn btn-primary m-1"
                              disabled={createProductMutation.isLoading}
                            >
                              {createProductMutation.isLoading ? (
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                              ) : (
                                "Create Product"
                              )}
                            </button>
                            <Link to="/products" className="btn btn-dark m-1">
                              Go Back
                            </Link>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    </>
  );
}